import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AuthService } from '@fleet/auth/services/auth.service';
import {
  FuseMediaWatcherService,
  FuseNavigationItem,
  FuseNavigationService,
  FuseVerticalNavigationComponent,
} from '@fleet/fuse';
import { GroupHierarchyItem } from '@fleet/model';
import { FleetNavigationService } from '@fleet/navigation';
import { ProductConfigurationService } from '@fleet/product-configuration';
import { HotkeysService } from '@fleet/shared';
import { TranslocoService } from '@jsverse/transloco';
import { combineLatest, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'fleet-main-layout',
    templateUrl: './fleet-layout.component.html',
    styleUrls: ['./fleet-layout.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class FleetLayoutComponent implements OnInit {
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  isScreenSmall: boolean;
  navigation: FuseNavigationItem[];
  currentGroup: GroupHierarchyItem;
  fleetProduct: string;
  envName: string;
  logoSrc: string = 'assets/logos/fleet-driver-horiz.svg';
  constructor(
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private _fuseNavigationService: FuseNavigationService,
    private fleetNavigationService: FleetNavigationService,
    private changeDetectorRef: ChangeDetectorRef,
    private _productConfigurationService: ProductConfigurationService,
    private authService: AuthService,
    private translocoService: TranslocoService,
    private hotkeysService: HotkeysService,
    private router: Router,
    @Inject('env') env: any
  ) {
    this.fleetProduct = env.fleetProduct;
    this.envName = env.envName;

    this.checkIfLanguageLoaded();

    combineLatest([
      this.translocoService.events$.pipe(
        filter(
          (event: any) =>
            event.type === 'translationLoadSuccess' &&
            event.payload.scope === 'navigation'
        )
      ),
      this.fleetNavigationService.navigation$,
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([translocoEvent, navigation]) => {
        if (navigation) {
          this.navigation = navigation;
        }
        this.changeDetectorRef.markForCheck();
      });
  }

  checkIfLanguageLoaded(): void {
    const isLoaded = this.translocoService.getActiveLang();
    if (isLoaded) {
      this.fleetNavigationService.navigation$
        .pipe(takeUntilDestroyed())
        .subscribe({
          next: (navigation: FuseNavigationItem[]) => {
            if (navigation) {
              this.navigation = navigation;
            }

            this.changeDetectorRef.markForCheck();
          },
        });
    }
  }

  ngOnInit(): void {
    if (this.fleetProduct === 'HUB') {
      this.registerHotkeys();
    }

    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('md');
      });

    this.fleetNavigationService.currentGroup$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (currentGroup: GroupHierarchyItem) => {
          this.currentGroup = currentGroup;
        },
      });

    this._productConfigurationService.config$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (config: any) => {
          if (config.horizontalMonoLogo) {
            this.logoSrc = config.horizontalMonoLogo;
            this.changeDetectorRef.markForCheck();
          }
        },
      });
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  registerHotkeys() {
    this.hotkeysService
      .addShortcut({ keys: 'F9' })
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: () => {
          //job search
          this.router.navigate(['/job/search']);
        },
      });

    this.hotkeysService
      .addShortcut({ keys: 'F10' })
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: () => {
          //driver searcg
          this.router.navigate(['/driver/search']);
        },
      });

    this.hotkeysService
      .addShortcut({ keys: 'F11' })
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: () => {
          //vehicle search
          this.router.navigate(['/vehicle/search']);
        },
      });

    this.hotkeysService
      .addShortcut({ keys: 'F12' })
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: () => {
          //notification search
          this.router.navigate(['/notification/search']);
        },
      });
  }

  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
        name
      );

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  get currentYear(): number {
    return new Date().getFullYear();
  }

  onNewJobClick(): void {
    const queryParams: any = {};

    // Get all route parameters from the route tree using router state
    const getAllRouteParams = (): any => {
      let params = {};

      // Start from root and traverse all routes
      let route = this.router.routerState.root;
      while (route) {
        params = { ...params, ...route.snapshot.params };
        route = route.firstChild;
      }

      return params;
    };

    const allParams = getAllRouteParams();

    // Check for traveller ID
    if (allParams['travellerId']) {
      queryParams.travellerId = allParams['travellerId'];
    }

    // Check for organisation ID
    if (allParams['organisationId']) {
      queryParams.organisationId = allParams['organisationId'];
    }

    // Navigate to job/new with query params if any were found
    this.router.navigate(['/job/new'], { queryParams });
  }
}
